import { chooseColumnCount } from "./layout.js";
import { downloadBytes, slugify } from "./download.js";
import { loadPdfFonts } from "./fonts.js";
import type { PdfDocModel, PdfUserOptions } from "./model.js";

type PdfRendererModule = typeof import("./renderPdf.js");

let rendererPromise: Promise<PdfRendererModule> | undefined;

function loadRenderer(): Promise<PdfRendererModule> {
  rendererPromise ??= import("./renderPdf.js");
  return rendererPromise;
}

function exportEntity(doc: PdfDocModel): "checklist" | "list" | "template" {
  if (doc.eyebrow.endsWith("TEMPLATE")) return "template";
  return doc.meta.some((item) => /^\d+ sections$/.test(item))
    ? "checklist"
    : "list";
}

function itemCount(doc: PdfDocModel): number {
  return doc.sections.reduce(
    (total, section) =>
      total +
      section.groups.reduce(
        (sectionTotal, group) => sectionTotal + group.items.length,
        0
      ),
    0
  );
}

async function logPdfExport(
  doc: PdfDocModel,
  options: PdfUserOptions,
  bytes: Uint8Array,
  fontFallback: boolean,
  durationMs: number
): Promise<void> {
  try {
    const { PDFDocument } = await import("pdf-lib");
    const pages = (await PDFDocument.load(bytes)).getPageCount();
    console.info({
      event: "pdf.export",
      entity: exportEntity(doc),
      itemCount: itemCount(doc),
      sectionCount: doc.sections.length,
      columns: chooseColumnCount(doc, options.includeNotes),
      pages,
      paper: options.paper,
      showProgress: options.showProgress,
      colorAccents: options.colorAccents,
      fontFallback,
      durationMs,
    });
  } catch {
    // Telemetry must never turn a successful browser download into a failure.
  }
}

/** Warms the lazy renderer chunk and browser font cache. Safe to call repeatedly. */
export async function warmPdfExport(): Promise<void> {
  try {
    await Promise.all([loadRenderer(), loadPdfFonts()]);
  } catch {
    // exportPdf retries the renderer import and reports a typed error if it still fails.
  }
}

export class PdfExportError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "PdfExportError";
  }
}

/** Renders the document and triggers a browser download. */
export async function exportPdf(
  doc: PdfDocModel,
  options: PdfUserOptions
): Promise<void> {
  const startedAt = Date.now();

  try {
    const [renderer, fonts] = await Promise.all([loadRenderer(), loadPdfFonts()]);
    const bytes = await renderer.renderPdf(doc, { ...options, fonts });
    downloadBytes(bytes, `${slugify(doc.title)}.pdf`);
    void logPdfExport(
      doc,
      options,
      bytes,
      fonts.regular === null || fonts.semibold === null,
      Date.now() - startedAt
    );
  } catch (error) {
    throw new PdfExportError("Could not create the PDF. Please try again.", error);
  }
}
